import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SkillsAnalysis } from '../../types';

interface SkillsSectionProps {
  data: SkillsAnalysis;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({ data }) => {
  const { t } = useTranslation();
  const getSkillLevelEmoji = (level: string): string => {
    switch(level) {
      case 'expert': return '🔥';
      case 'advanced': return '⭐';
      case 'intermediate': return '✓';
      case 'basic': return '•';
      default: return '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">💻</span>
        <h3 className="text-xl font-semibold">{t('results.skills.title')}</h3>
        <span className="ml-auto text-xl font-bold">{data?.score}%</span>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-green-500">
            <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
              <span className="mr-2">✅</span>
              {t('results.skills.found', { count: data?.found?.length })}
            </h4>
            
            <div className="flex flex-wrap gap-2">
              {data?.found?.map((skill, index) => (
                <span 
                  key={index}
                  className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300"
                  title={`Niveau: ${skill?.level}, Score: ${Math.round(skill?.score * 100)}%`}
                >
                  {skill?.found !== 'none' && skill?.found !== 'undefined' ? skill.found : ''} {getSkillLevelEmoji(skill?.level)}
                </span> 
              ))}
              
              {data?.found?.length === 0 && (
                <p className="text-gray-500 italic">{t('results.skills.noneFound')}</p>
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-red-500">
            <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
              <span className="mr-2">❌</span>
              {t('results.skills.missing', { count: data?.missing?.length })}
            </h4>
            
            <div className="flex flex-wrap gap-2">
              {data?.missing?.map((item, index) => (
                <span 
                  key={index}
                  className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-300"
                >
                  {item?.skill}
                </span>
              ))}
              
              {data?.missing?.length === 0 && (
                <p className="text-green-600 font-medium">{t('results.skills.noneMissing')}</p>
              )}
            </div>
          </div>
          
          {data?.bonus?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-blue-500 md:col-span-2">
              <h4 className="font-semibold text-lg mb-3 flex items-center text-gray-800">
                <span className="mr-2">🎁</span>
                {t('results.skills.bonus', { count: data?.bonus?.length })}
              </h4>
              
              <div className="flex flex-wrap gap-2">
                {data?.bonus?.map((item, index) => (
                  <span 
                    key={index}
                    className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-300"
                  >
                    {item?.skill}
                  </span>
                ))}
              </div>
            </div>
          )}
          {data?.bonus?.length === 0 && (
            <p className="text-blue-600 font-medium">{t('results.skills.noBonus')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillsSection;